import Image from "next/image";

import type { BlockProps, StrapiBlock } from "@/app/components/blocks/types";
import { Heading } from "@/app/design-system/atoms/Heading";

type Media = {
  id?: number;
  documentId?: string;
  url?: string | null;
  alternativeText?: string | null;
};

type OffrePromo = {
  id?: number;
  documentId?: string;
  prixTTC?: string | null;
  prixHT?: string | null;
  prixApresAidesFiscales?: string | null;
  titreMenu?: string | null;
  image?: Media | null;
  nomPlat?: string | null;
  description?: string | null;
  dateDebut?: string | null;
  dateFin?: string | null;
};

type NosOffresBlock = StrapiBlock & {
  __component: "blocks.nos-offres";
  titre?: string | null;
  offresPromo?: OffrePromo[];
};

function isOfferActive(dateDebut?: string | null, dateFin?: string | null): boolean {
  const now = new Date();

  if (dateDebut) {
    const start = new Date(dateDebut);
    if (!Number.isNaN(start.getTime()) && now < start) return false;
  }

  if (dateFin) {
    const end = new Date(dateFin);
    if (!Number.isNaN(end.getTime()) && now > end) return false;
  }

  return true;
}

function PrixBadge({
  prixTTC,
  prixHT,
  prixApresAidesFiscales,
}: {
  prixTTC?: string | null;
  prixHT?: string | null;
  prixApresAidesFiscales?: string | null;
}) {
  if (!prixTTC && !prixHT && !prixApresAidesFiscales) return null;

  return (
    <div className="mt-2 grid w-full max-w-[520px] grid-cols-2 items-center rounded-xl bg-primaryforeground px-4 py-3 lg:mt-6 lg:rounded-2xl lg:px-5 lg:py-4">

      <div className="border-r border-primaryGreen/40 pr-3 lg:pr-5">
        {prixTTC && (
          <p className="text-[26px] font-bold leading-tight text-primaryGreen lg:text-4xl">
            {prixTTC} <span className="text-[20px] lg:text-[30px]">€ TTC</span>
          </p>
        )}

        {prixHT && (
          <p className="mt-1 text-base text-textColor lg:text-lg">
            ({prixHT} € HT)
          </p>
        )}
      </div>

      <div className="pl-3 lg:pl-5">
        {prixApresAidesFiscales && (
          <>
            <p className="text-[24px] font-bold leading-tight text-primaryGreen lg:text-[26px]">
              {prixApresAidesFiscales} €
            </p>

            <p className="mt-1 text-sm leading-tight text-textColor lg:text-base">
              après aides fiscales*
            </p>
          </>
        )}
      </div>
    </div>
  );
}
export default function NosOffres({ data }: BlockProps<NosOffresBlock>) {
  const offres = Array.isArray(data.offresPromo) ? data.offresPromo : [];
  const offresActives = offres.filter((offre) => isOfferActive(offre.dateDebut, offre.dateFin));

  if (offresActives.length === 0) return null;

  return (
    <section className="bg-white py-8 lg:py-20">
      <div className="lms-container">
        {data.titre && (
          <Heading as="h2" className="mb-[4px] lg:mb-6 text-center font-playfair text-4xl font-bold text-[#343434] lg:text-[44px] ">
            {data.titre}
          </Heading>
        )}

        <div className="mx-auto flex max-w-[1200px] flex-col gap-10">
          {offresActives.map((offre, index) => {
            const reversed = index % 2 !== 0;
            const imageUrl = offre.image?.url || "";
            const imageAlt = offre.image?.alternativeText || offre.nomPlat || offre.titreMenu || "Offre promotionnelle";

            return (
              <article
                key={offre.documentId ?? offre.id ?? index}
                className={`overflow-hidden rounded-2xl ${reversed
                  ? "border border-gray-200 bg-white"
                  : "bg-[#F6F6F6]"
                  }`}
              >
                <div className="grid gap-3 p-3 lg:grid-cols-2 lg:items-center lg:gap-14 lg:p-12">

                  {/* IMAGE */}
                  <div
                    className={`relative h-[160px] w-full overflow-hidden rounded-xl bg-rose-50 lg:h-auto lg:aspect-[4/3] lg:rounded-2xl ${reversed ? "lg:order-2" : "lg:order-1"
                      }`}
                  >
                    {imageUrl && (
                      <Image
                        src={imageUrl}
                        alt={imageAlt}
                        fill
                        className="object-cover"
                        sizes="(max-width: 1024px) 100vw, 560px"
                      />
                    )}
                  </div>

                  {/* CONTENU */}
                  <div
                    className={`flex flex-col items-start ${reversed ? "lg:order-1" : "lg:order-2"
                      }`}
                  >
                    {offre.titreMenu && (
                      <h3 className="font-roboto text-[22px] font-bold leading-tight text-primaryGreen lg:text-3xl">
                        {offre.titreMenu}
                      </h3>
                    )}

                    <div className="my-2 h-[3px] w-14 rounded-full bg-primaryGreen lg:my-5 lg:w-16" />

                    {offre.nomPlat && (
                      <h4 className="max-w-[420px] text-lg font-bold leading-tight text-[#343434] lg:text-xl">
                        {offre.nomPlat}
                      </h4>
                    )}

                    {offre.description && (
                      <p className="mt-1 max-w-[460px] text-base leading-6 text-textColor lg:mt-4 lg:text-lg lg:leading-relaxed">
                        {offre.description}
                      </p>
                    )}

                    <PrixBadge
                      prixTTC={offre.prixTTC}
                      prixHT={offre.prixHT}
                      prixApresAidesFiscales={offre.prixApresAidesFiscales}
                    />
                  </div>

                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}